import { useEffect, useState } from "react";
import styled from "styled-components";
import { AnimatePresence, motion } from "motion/react";
import {
  DESKTOP_APPS,
  DesktopApp,
  useOpenApp,
} from "../../pages/HomePage/components/BackgroundIcons/desktopApps";
import { PROFILE } from "../../config/profile";
import { playClick } from "../../utils/sound";
import windowsIcon from "../../assets/images/windows-0.png";
import directoryIcon from "../../assets/images/directory.png";
import gearsIcon from "../../assets/images/gears_tweakui_a-1.png";
import computerIcon from "../../assets/images/computer_explorer-3.png";
import msnIcon from "../../assets/images/msn3-4.png";
import MyComputer from "../../pages/HomePage/components/MyComputer/MyComputer";
import { useDesktop } from "../../pages/HomePage/utils/DesktopContext";
import { useSystemWindows } from "../../pages/HomePage/utils/useSystemWindows";

interface StartMenuProps {
  open: boolean;
  onClose: () => void;
}

const CONTROL_PANEL: DesktopApp = {
  name: "Control Panel",
  iconSrc: gearsIcon,
  component: <MyComputer initialPath="control" />,
};

const StartMenu = ({ open, onClose }: StartMenuProps) => {
  const openApp = useOpenApp();
  const { soundOn, toggleSound, shutDown } = useDesktop();
  const { openSystemProperties } = useSystemWindows();
  const [showGames, setShowGames] = useState(false);
  useEffect(() => {
    if (!open) setShowGames(false);
  }, [open]);

  const games = DESKTOP_APPS.filter((app) => app.isGame);
  const programs = DESKTOP_APPS.filter((app) => !app.isGame);

  const launch = (app: DesktopApp) => {
    playClick();
    openApp(app);
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <MenuContainer
          key="start-menu"
          onMouseLeave={() => setShowGames(false)}
        >
          <MenuPanel
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            exit={{
              clipPath: "inset(100% 0 0 0)",
              transition: { duration: 0.1 },
            }}
            transition={{ duration: 0.18, ease: "easeOut" }}
          >
            <Banner>
              <span>
                <b>{PROFILE.osName.split(" ")[0]}</b>{" "}
                {PROFILE.osName.split(" ").slice(1).join(" ")}
              </span>
            </Banner>
            <Items>
              <Item
                onMouseEnter={() => setShowGames(true)}
                $highlight={showGames}
              >
                <ItemIcon src={directoryIcon} alt="" />
                <span>
                  <u>G</u>ames
                </span>
                <Arrow>▸</Arrow>
              </Item>
              {programs.map((app) => (
                <Item
                  key={app.name}
                  onMouseEnter={() => setShowGames(false)}
                  onClick={() => launch(app)}
                >
                  <ItemIcon src={app.iconSrc} alt="" />
                  {app.name}
                </Item>
              ))}
              <Separator />
              <Item
                onMouseEnter={() => setShowGames(false)}
                onClick={() => launch(CONTROL_PANEL)}
              >
                <ItemIcon src={gearsIcon} alt="" />
                <span>Control Panel</span>
              </Item>
              <Item
                onClick={() => {
                  onClose();
                  openSystemProperties();
                }}
              >
                <ItemIcon src={computerIcon} alt="" />
                <span>About This Computer</span>
              </Item>
              <Item onClick={toggleSound}>
                <ItemIcon src={msnIcon} alt="" />
                <span>Sounds: {soundOn ? "On" : "Off"}</span>
              </Item>
              <Separator />
              <Item
                onClick={() => {
                  onClose();
                  shutDown();
                }}
              >
                <ItemIcon src={windowsIcon} alt="" />
                <span>
                  Sh<u>u</u>t Down...
                </span>
              </Item>
            </Items>
          </MenuPanel>
          <AnimatePresence>
            {showGames && (
              <SubMenu
                initial={{ clipPath: "inset(0 100% 0 0)" }}
                animate={{ clipPath: "inset(0 0% 0 0)" }}
                exit={{ opacity: 0, transition: { duration: 0.08 } }}
                transition={{ duration: 0.15 }}
              >
                {games.map((game) => (
                  <Item key={game.name} onClick={() => launch(game)} $small>
                    <ItemIcon src={game.iconSrc} alt="" $small />
                    {game.name}
                  </Item>
                ))}
              </SubMenu>
            )}
          </AnimatePresence>
        </MenuContainer>
      )}
    </AnimatePresence>
  );
};

export default StartMenu;

const MenuContainer = styled.div`
  position: absolute;
  bottom: calc(100% + 2px);
  left: 2px;
  z-index: 5;
`;

const MenuPanel = styled(motion.div)`
  display: flex;
  min-width: 230px;
  background: ${(p) => p.theme.MSBackground};
  box-shadow: ${(p) => p.theme.standardBoxShadow};
  padding: 3px;
`;

const Banner = styled.div`
  width: 28px;
  background: linear-gradient(0deg, #00007b 0%, #1085d2 100%);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 8px;

  & span {
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    color: #c0c0c0;
    font-size: 20px;
    letter-spacing: 1px;
    white-space: nowrap;
  }

  & b {
    color: #fff;
    font-weight: 700;
  }
`;

const Items = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Item = styled.div<{ $highlight?: boolean; $small?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: ${(p) => (p.$small ? "4px 16px 4px 6px" : "6px 24px 6px 6px")};
  font-size: ${(p) => p.theme.fontNormal}px;
  cursor: pointer;
  white-space: nowrap;
  background: ${(p) => (p.$highlight ? "#00007b" : "transparent")};
  color: ${(p) => (p.$highlight ? "#fff" : "#000")};

  &:hover {
    background: #00007b;
    color: #fff;
  }
`;

const ItemIcon = styled.img<{ $small?: boolean }>`
  width: ${(p) => (p.$small ? 18 : 28)}px;
  height: ${(p) => (p.$small ? 18 : 28)}px;
`;

const Arrow = styled.span`
  margin-left: auto;
  font-size: 10px;
`;

const Separator = styled.div`
  height: 2px;
  margin: 3px 2px;
  border-top: 1px solid #808080;
  border-bottom: 1px solid #fff;
`;

const SubMenu = styled(motion.div)`
  position: absolute;
  left: calc(100% - 3px);
  top: 0;
  min-width: 160px;
  background: ${(p) => p.theme.MSBackground};
  box-shadow: ${(p) => p.theme.standardBoxShadow};
  padding: 3px;
  color: #000;
`;
